import { type Um } from './units.js';
import type { EdgeId } from './model.js';
import type { EdgeDecision } from './edgebanding.js';

/**
 * Supplier measurement convention. Applied only at export time — never changes the project.
 *   finished  — supplier receives finished size and subtracts banding itself
 *   substrate — supplier receives raw substrate size (finished − band thickness)
 *   cut       — supplier receives the saw cut size (substrate + pre-milling allowance)
 */
export interface MeasurementProfile {
  id: string;
  label: string;
  orderSize: 'finished' | 'substrate' | 'cut';
  /** Pre-milling allowance added per banded edge to the cut size. */
  premillPerBandedEdge: Um;
  /** Edge band overhang added per banded edge for material ordering (trimmed off after banding). */
  edgeOverhangPerEdge: Um;
  /** Extra edge band waste in percent of gross length. */
  edgeWastePercent: number;
  /** Rounding step for ordered sizes (e.g. 1000 µm = 1 mm). 0 = none. */
  roundingStep: Um;
  rounding: 'nearest' | 'down' | 'up';
}

export const PROFILE_SUBSTRATE: MeasurementProfile = {
  id: 'order-substrate',
  label: 'Narudžba sirove podloge',
  orderSize: 'substrate',
  premillPerBandedEdge: 0,
  edgeOverhangPerEdge: 0,
  edgeWastePercent: 0,
  roundingStep: 0,
  rounding: 'nearest',
};

export const PROFILE_FINISHED: MeasurementProfile = {
  id: 'order-finished',
  label: 'Narudžba gotove mjere, radnja oduzima kant',
  orderSize: 'finished',
  premillPerBandedEdge: 0,
  edgeOverhangPerEdge: 0,
  edgeWastePercent: 0,
  roundingStep: 0,
  rounding: 'nearest',
};

export interface PartSizes {
  finished: { length: Um; width: Um };
  substrate: { length: Um; width: Um };
  cut: { length: Um; width: Um };
  order: { length: Um; width: Um };
}

function bandT(d: EdgeDecision | undefined): Um {
  return d && d.decision === 'band' && d.band ? d.band.thickness : 0;
}

function round(v: Um, p: MeasurementProfile): Um {
  if (p.roundingStep <= 0) return v;
  const q = v / p.roundingStep;
  const r = p.rounding === 'down' ? Math.floor(q) : p.rounding === 'up' ? Math.ceil(q) : Math.round(q);
  return r * p.roundingStep;
}

/**
 * Banding thickness is subtracted exactly once (finished → substrate) and pre-milling is added
 * exactly once (substrate → cut). The order size picks one of these, then rounds.
 * L-edges run along the length → they reduce the WIDTH; W-edges reduce the LENGTH.
 */
export function computePartSizes(
  finished: { length: Um; width: Um },
  decisions: Partial<Record<EdgeId, EdgeDecision>>,
  profile: MeasurementProfile,
): PartSizes {
  const lenBands = bandT(decisions.W1) + bandT(decisions.W2);
  const widBands = bandT(decisions.L1) + bandT(decisions.L2);
  const lenBanded = (bandT(decisions.W1) > 0 ? 1 : 0) + (bandT(decisions.W2) > 0 ? 1 : 0);
  const widBanded = (bandT(decisions.L1) > 0 ? 1 : 0) + (bandT(decisions.L2) > 0 ? 1 : 0);
  const substrate = { length: finished.length - lenBands, width: finished.width - widBands };
  const cut = {
    length: substrate.length + lenBanded * profile.premillPerBandedEdge,
    width: substrate.width + widBanded * profile.premillPerBandedEdge,
  };
  const base = profile.orderSize === 'finished' ? finished : profile.orderSize === 'substrate' ? substrate : cut;
  return {
    finished: { ...finished },
    substrate,
    cut,
    order: { length: round(base.length, profile), width: round(base.width, profile) },
  };
}
